import { Suspense, useMemo } from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import {
  getQueryClientDefaultOptions,
  loadCustomScrollbarPlugins,
  loadStyles,
} from '@uniqkey-frontend/shared-app';
import './helpers/ChartJSLib/config';
import featureConfig from './featureConfig';
import MigrationPageRouteEnum from './migrationModules/enums/MigrationPageRouteEnum';
import OnboardingPageRouteEnum from './modules/OnboardingModule/enums/OnboardingPageRouteEnum';
import UserProviderContext from './contexts/UserContext/provider';
import Router from './Router';
import APIClientsProviderContext from './contexts/APIClientsContext/provider';
import CompanionApplicationProviderContext from './contexts/CompanionApplicationContext/provider';
import InitializationContainer from './components/InitializationContainer';
import GoogleMapsScriptLoader from './components/GoogleMaps/GoogleMapsScriptLoader';
import { listenRefreshTokensEvents } from './services/tokensManager';
import MigrationApp from './migrationModules/App';
import OnboardingApp from './modules/OnboardingModule/App';

loadStyles({
  heroThemeColors: true,
  dateRangePicker: true,
  heroSnackbar: true,
  reactCircularProgressbar: true,
  customScrollbar: true,
});

loadCustomScrollbarPlugins();

listenRefreshTokensEvents();

const ReactQueryClient = new QueryClient({
  defaultOptions: getQueryClientDefaultOptions(),
});

const App = () => {
  const { isMigration, isOnboarding } = useMemo(() => {
    const { pathname } = window.location;
    return {
      isMigration: pathname.startsWith(MigrationPageRouteEnum.Migration),
      isOnboarding: featureConfig.onboardingSettings && pathname.startsWith(
        OnboardingPageRouteEnum.Onboarding,
      ),
    };
  }, []);

  if (isMigration) {
    return <MigrationApp />;
  }
  if (isOnboarding) {
    return <OnboardingApp />;
  }

  return (
    <QueryClientProvider client={ReactQueryClient}>
      <UserProviderContext>
        <APIClientsProviderContext>
          <CompanionApplicationProviderContext>
            <InitializationContainer>
              <DndProvider backend={HTML5Backend}>
                <GoogleMapsScriptLoader>
                  <Suspense fallback={null}>
                    <Router />
                  </Suspense>
                  <ReactQueryDevtools />
                </GoogleMapsScriptLoader>
              </DndProvider>
            </InitializationContainer>
          </CompanionApplicationProviderContext>
        </APIClientsProviderContext>
      </UserProviderContext>
    </QueryClientProvider>
  );
};

export default App;
